/*
 * Copyright French Prime minister Office/SGMAP/DINSIC/Vitam Program (2019-2022)
 * and the signatories of the "VITAM - Accord du Contributeur" agreement.
 *
 * contact@programmevitam.fr
 *
 * This software is a computer program whose purpose is to implement
 * implement a digital archiving front-office system for the secure and
 * efficient high volumetry VITAM solution.
 *
 * This software is governed by the CeCILL-C license under French law and
 * abiding by the rules of distribution of free software.  You can  use,
 * modify and/ or redistribute the software under the terms of the CeCILL-C
 * license as circulated by CEA, CNRS and INRIA at the following URL
 * "http://www.cecill.info".
 *
 * As a counterpart to the access to the source code and  rights to copy,
 * modify and redistribute granted by the license, users are provided only
 * with a limited warranty  and the software's author,  the holder of the
 * economic rights,  and the successive licensors  have only  limited
 * liability.
 *
 * In this respect, the user's attention is drawn to the risks associated
 * with loading,  using,  modifying and/or developing or reproducing the
 * software by the user in light of its specific status of free software,
 * that may mean  that it is complicated to manipulate,  and  that  also
 * therefore means  that it is reserved for developers  and  experienced
 * professionals having in-depth computer knowledge. Users are therefore
 * encouraged to load and test the software's suitability as regards their
 * requirements in conditions enabling the security of their systems and/or
 * data to be ensured and,  more generally, to use and operate it in the
 * same conditions as regards security.
 *
 * The fact that you are presently reading this means that you have had
 * knowledge of the CeCILL-C license and that you accept its terms.
 */
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BASE_URL } from '../injection-tokens';
import { ApiEvent } from '../models/logbook/api-event.interface';
import { HistoryEvent, IEvent } from '../models/logbook/event.interface';
import { VitamResponse } from '../models/vitam/vitam-response.interface';
import { PaginatedApi } from '../paginated-api.interface';
import { PageRequest } from '../vitamui-table/page-request.model';
import { PaginatedResponse } from '../vitamui-table/paginated-response.interface';

const CAS_CONTEXT = 'Contexte CAS';
const UNKNOWN_VALUE = '-';
const USER_INDEX = 3;
const SUBROGATOR_INDEX = 4;
const API_CONTEXT_INDEX = 2;

export type LogbookDownloadType = 'report' | 'batchreport' | 'object' | 'dip' | 'transfersip' | 'atr' | 'manifest';

@Injectable({
  providedIn: 'root',
})
export class LogbookApiService implements PaginatedApi<IEvent> {
  private http = inject(HttpClient);

  private readonly apiUrl: string;
  private readonly baseUrl: string;

  constructor() {
    const baseUrl = inject(BASE_URL);

    this.apiUrl = baseUrl + '/logbooks';
    this.baseUrl = baseUrl;
  }

  public static toEvent(apiEvent: ApiEvent): IEvent {
    const user: string = LogbookApiService.getUserIdentifier(apiEvent.evIdAppSession, USER_INDEX);
    const subrogator: string = LogbookApiService.getUserIdentifier(apiEvent.evIdAppSession, SUBROGATOR_INDEX);
    return {
      id: apiEvent.evId,
      obIdIn: apiEvent.obIdIn,
      idRequest: apiEvent.evIdReq,
      parentId: apiEvent.evParentId,
      type: apiEvent.evType,
      typeProc: apiEvent.evTypeProc,
      dateTime: apiEvent.evDateTime,
      outcome: apiEvent.outcome,
      outDetail: apiEvent.outDetail,
      outMessage: apiEvent.outMessg,
      data: apiEvent.evDetData,
      parsedData: JSON.parse(apiEvent.evDetData),
      objectId: apiEvent.obId,
      collectionName: apiEvent.obIdReq,
      agId: apiEvent.agId,
      agIdApp: apiEvent.agIdApp,
      agIdExt: apiEvent.agIdExt,
      obIdReq: apiEvent.obIdReq,
      obId: apiEvent.obId,
      rightsStatementIdentifier: apiEvent.rightsStatementIdentifier,
      userIdentifier: user,
      subrogatorIdentifier: subrogator,
      events: LogbookApiService.getSubEvents(apiEvent.events || [], user, subrogator),
    };
  }

  private static getSubEvents(apiEvents: ApiEvent[], userIdentifier: string, subrogatorIdentifier: string): IEvent[] {
    const result: IEvent[] = [];
    apiEvents.forEach((apiEvent) => {
      const event: IEvent = LogbookApiService.toEvent(apiEvent);
      event.userIdentifier = userIdentifier;
      event.subrogatorIdentifier = subrogatorIdentifier;
      result.push(event);
    });
    return result;
  }

  private static getUserIdentifier(evIdAppSession: string, userIndex: number): string {
    let userIdentifier: string;
    if (evIdAppSession) {
      // evIdAppSession format : API_ID:SESSSION_ID:API_CONTEXT:USER_ID:SUBROGATOR_ID:?
      // evIdAppSession example : CUSTOMERS_APP101763287410:973862d2-12c1-4244-9ed9-0cd42627e74b:Contexte UI Identity:101:-:1
      const data: string[] = evIdAppSession.split(':');
      if (data && data[API_CONTEXT_INDEX] !== CAS_CONTEXT && data[userIndex] !== UNKNOWN_VALUE) {
        userIdentifier = data[userIndex];
      }
    }
    return userIdentifier;
  }

  findOperationById(operationId: string, headers?: HttpHeaders): Observable<{ $results: any[] }> {
    return this.http.get<{ $results: any[] }>(this.apiUrl + '/operations/' + operationId, { headers });
  }

  findOperationByIdAndCollectionName(id: string, resourcePath: string, headers?: HttpHeaders): Observable<HistoryEvent[]> {
    return this.http
      .get<HistoryEvent[]>(this.baseUrl + '/' + resourcePath + '/' + id + '/history', { headers })
      .pipe(map((events) => events.map((event) => ({ ...event, parsedData: JSON.parse(event.data) }))));
  }

  downloadManifest(id: string, headers?: HttpHeaders): Observable<HttpResponse<Blob>> {
    return this.http.get(this.apiUrl + '/operations/' + id + '/download/manifest', { headers, observe: 'response', responseType: 'blob' });
  }

  downloadAtr(id: string, headers?: HttpHeaders): Observable<HttpResponse<Blob>> {
    return this.http.get(this.apiUrl + '/operations/' + id + '/download/atr', { headers, observe: 'response', responseType: 'blob' });
  }

  prepareSignedDownload(id: string, downloadType: LogbookDownloadType, headers?: HttpHeaders): Observable<string> {
    return this.http
      .post(`${this.apiUrl}/operations/${id}/download/${downloadType}/signed-url`, null, { headers, responseType: 'text' })
      .pipe(map((url) => `${this.baseUrl}${url}`));
  }

  getAllPaginated(pageRequest: PageRequest, _?: string, headers?: HttpHeaders): Observable<PaginatedResponse<IEvent>> {
    const criteria = JSON.parse(pageRequest.criteria);
    criteria.$filter.$offset = pageRequest.page * pageRequest.size;
    pageRequest.criteria = JSON.stringify(criteria);

    // The pagination and order are defined in the Vitam DSL query stored in the `criteria` property
    // We don't actually need to use the other properties of the page request
    return this.http.post<VitamResponse<ApiEvent>>(this.apiUrl + '/operations', JSON.parse(pageRequest.criteria), { headers }).pipe(
      map((response) => ({
        pageNum: pageRequest.page,
        pageSize: pageRequest.size,
        totalElements: response.$hits.total,
        hasMore: response.$hits.offset + response.$hits.size < response.$hits.total,
        values: response.$results.map(LogbookApiService.toEvent),
      })),
    );
  }
}
